import { useQuery } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { FC, useState } from 'react';
import styled from 'styled-components';

import { CloseButton } from '@/components/AlertDialogs/Dialog';
import ContentSpinner from '@/components/ContentSpinner';
import { Modal } from '@/components/Modal';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import { ReleaseResource } from '@/types/resources';
import { api } from '@/utils/api';
import { hexToRgba } from '@/utils/theme';

import DistributionListItem from './DistributionListItem';

const Dialog = styled(motion.div)`
  width: 90%;
  max-width: 1120px;
  padding-top: 56px;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.white};
  position: relative;
  max-height: 600px;
  overflow-y: auto;
`;

const StyledContentSpinner = styled(ContentSpinner)`
  margin: 100px 0;
`;

const BadgeButton = styled.button`
  background: ${({ theme }) => hexToRgba(theme.colors.purple, 0.08)};
  padding: 5px 11px;
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.purple};
  display: inline-flex;
  align-items: center;
  border: 1px solid transparent;
  gap: 8px;
  transition: 0.2s ease-out border-color;
  &:hover {
    border-color: ${({ theme }) => theme.colors.purple};
  }
`;

interface Props {
  releaseId: number;
  className?: string;
}

const ConditionalRepostsButton: FC<Props> = ({ releaseId, className }) => {
  const [showModal, setShowModal] = useState(false);

  const alerts = useAlerts();

  const { data: release } = useQuery(
    [
      'releases',
      {
        id: releaseId,
      },
    ],
    () => api.get<never, ReleaseResource>(`/v1/releases/${releaseId}`),
    {
      onError: alerts.error,
      staleTime: 60 * 1000,
    },
  );

  return (
    <>
      <BadgeButton
        type="button"
        onClick={() => setShowModal(true)}
        className={className}
      >
        <Text as="span" $color="purple" $type="smallHeadline">
          Conditional reposts included
        </Text>
        <svg
          width="12"
          height="13"
          viewBox="0 0 12 13"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle
            cx="6"
            cy="6.5"
            r="5.5"
            fill="currentColor"
            stroke="currentColor"
          />
          <path
            d="M5.5 4.80147V3.875H6.5V4.80147H5.5ZM5.5 9.125V5.41912H6.5V9.125H5.5Z"
            fill="white"
          />
        </svg>
      </BadgeButton>
      <AnimatePresence>
        {showModal && (
          <Modal>
            <Dialog
              initial={{ scale: 0.3 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.3 }}
            >
              <CloseButton onClick={() => setShowModal(false)}>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M13 1L1 13"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M13 13L1 1"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </CloseButton>
              {!release ? (
                <StyledContentSpinner />
              ) : (
                <DistributionListItem showRepostsDetails release={release} />
              )}
            </Dialog>
          </Modal>
        )}
      </AnimatePresence>
    </>
  );
};

export default ConditionalRepostsButton;
